import React, { useState, useCallback, useEffect } from "react";
import {
  Badge,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Wrap,
  WrapItem,
  useToast,
} from "@chakra-ui/react";
import { organizationService } from "api/organization-administration";
import { useQuery, useQueryClient } from "react-query";
import { esgAdminKeys } from "hooks/query/esg-admin/esg-admin";
import { useAuth } from "context";
import useAirUserRoles from "utils/AuthUtils";
import LocalSpinner from "components/LocalSpinner";
import { useRerender } from "rerenderer";
import PropInput from "components/PropInput";

import DimensionsEditorPanel from "./DimensionsEditorPanel";

export default function EditOrganizationModal({ organization, isOpen, onClose }) {
  const tenantId = organization?.tenantId;
  const { isOrgAdmin, isESGAdmin } = useAirUserRoles();
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { rerender } = useRerender();

  const [localOrg, setLocalOrg] = useState();
  const [isModified, setIsModified] = useState(false);

  const { data: org, isLoading } = useQuery({
    queryKey: esgAdminKeys.orgs().concat(tenantId),
    queryFn: async () => {
      await getAccessTokenSilently();
      return organizationService.get(tenantId);
    },
    enabled: !!tenantId && isOpen,
    onSuccess: (data) => {
      if (!data.settings) {
        data.settings = {};
      }
      setLocalOrg(data);
      setIsModified(false);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setLocalOrg(undefined);
      setIsModified(false);
    }
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (!localOrg) return;
    organizationService
      .update(tenantId, localOrg)
      .then((data) => {
        setLocalOrg(data);
        setIsModified(false);
        queryClient.invalidateQueries(esgAdminKeys.orgs());
        toast({
          title: "Organization updated",
          description: `Organization ${data.name} has been updated successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      })
      .catch((error) => {
        toast({
          title: "Error updating organization",
          description: error?.message || "An error occurred while updating the organization.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [tenantId, localOrg, toast, onClose, queryClient]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <ModalHeader>Edit Organization {org?.name ?? ""}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoading || !localOrg ? (
            <LocalSpinner />
          ) : (
            <Flex direction="column" gap={4}>
              <Text fontSize="xs" color="gray.500" fontFamily="mono">ID: {tenantId}</Text>

              <Wrap spacing={2}>
                <WrapItem>
                  <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme={isOrgAdmin ? "green" : "red"}>
                    {isOrgAdmin ? "Org Admin" : "Not Org Admin"}
                  </Badge>
                </WrapItem>
                <WrapItem>
                  <Badge borderRadius="md" px={2} py={0.5} fontSize="xs" colorScheme={isESGAdmin ? "green" : "red"}>
                    {isESGAdmin ? "ESG Admin" : "Not ESG Admin"}
                  </Badge>
                </WrapItem>
              </Wrap>

              <PropInput
                label="Organization Logo URL"
                type="text"
                help="URL for the logo of the organization, e.g. https://example.com/logo.jpeg"
                prop="logoUrl"
                item={localOrg}
                onChange={() => {
                  setIsModified(true);
                  rerender();
                }}
              />

              <PropInput
                label="AI: Custom Prompt Snippet"
                type="textarea"
                help="Optional AI customisation - inserted into the main prompt. Can be used for e.g. tone of voice"
                prop="aiCustomPromptSnippet"
                item={localOrg?.settings}
                onChange={() => {
                  setIsModified(true);
                  rerender();
                }}
              />

              <DimensionsEditorPanel org={localOrg} />
            </Flex>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!isModified}>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
